<template>
  <div class="table-container">
    <div class="table-controls-list d-flex justify-content-between align-items-center mb-4">
      <div class="table-controls-item filter-datepicker d-flex align-items-center">
        <b-button
          variant="light"
          :disabled="isDataLoading"
          @click.prevent="changeWeek('prev')"
        >
          ‹
        </b-button>
        <div
          class="d-flex align-items-center filter-dateweek mx-2"
        >
          <Datepicker
            class="cursor-pointer"
            :bootstrap-styling="true"
            :monday-first="true"
            :value="dates.start"
            disabled
            :disabled-dates="disabledFirstDate"
            :format="'dd/MM/yy'"
            @selected="(v) => selectDates(v, 'start')"
          />
          -
          <Datepicker
            class="cursor-pointer"
            :bootstrap-styling="true"
            :monday-first="true"
            :value="dates.end"
            disabled
            :disabled-dates="disabledSecondDate"
            :format="'dd/MM/yy'"
            @selected="(v) => selectDates(v, 'end')"
          />
        </div>
        <b-button
          variant="light"
          :disabled="disabledBtn === 0 || isDataLoading"
          @click.prevent="changeWeek('next')"
        >
          ›
        </b-button>
      </div>
      <div class="d-flex align-items-center ml-4" v-if="!isDataLoading && managers.length">
        <div class="ml-3 table-control d-flex align-items-center" @click="showIncorrectLoggedHoursOnly = !showIncorrectLoggedHoursOnly">
          <label class="form__label cursor-pointer">Incorrect logged hours only</label>
          <b-button
            variant="secondary"
            class="button btn-icon button-checkbox_sm ml-2"
          >
            <b-form-checkbox
              size="sm"
              switch
              disabled
              v-model="showIncorrectLoggedHoursOnly"
              class="switch"
            />
          </b-button>
        </div>
        <div class="ml-3 table-control d-flex align-items-center" @click="showIncorrectCommercialHoursOnly = !showIncorrectCommercialHoursOnly">
          <label class="form__label cursor-pointer">Incorrect commercial hours only</label>
          <b-button
            variant="secondary"
            class="button btn-icon button-checkbox_sm ml-2"
          >
            <b-form-checkbox
              size="sm"
              switch
              disabled
              v-model="showIncorrectCommercialHoursOnly"
              class="switch"
            />
          </b-button>
        </div>
        <UsersStateListComponent
          class="mx-4"
          :table-name="uiTableName"
          :members-list="members.map(member => member.user)"
          @changeActive="changeActive($event)"
        />
      </div>
    </div>
    <b-table-simple
      v-if="!isDataLoading && managers.length && getMembers.length"
      class="table__theme-default table__hours-tracking position-relative"
      :class="{'w-75': getMembers.length > 2 && getMembers.length <= 4, 'w-50': getMembers.length <= 2}"
      :fixed="false"
      stripped
      responsive
    >
      <colgroup><col></colgroup>
      <colgroup><col></colgroup>
      <template v-for="member in getMembers">
        <colgroup class="group-border-left" :key="`${member.user.name}`"><col><col><col></colgroup>
      </template>

      <b-thead head-variant="light">
        <b-tr>
          <b-th class="text-center text-lowercase first-sticky" sticky-column></b-th>
          <b-th class="text-center text-lowercase second-sticky" sticky-column></b-th>
          <template v-for="(member, i) in getMembers">
            <b-th class="member-name" :key="`${member.user.name}-${i}`" colspan="3" variant="secondary" v-if="userState[member.user.id]">
              <div>
                <TableUserItem class="text-center m-auto" :user="member.user" :item-max-width="'180px'" />
              </div>
            </b-th>
          </template>
        </b-tr>
        <b-tr>
          <b-th class="text-center text-lowercase managers-column first-sticky" sticky-column>Managers</b-th>
          <b-th class="text-center text-lowercase second-sticky" sticky-column>Logged hours</b-th>
          <template v-for="member in getMembers" variant="secondary">
            <b-th class="text-center text-lowercase" :key="`${member.user.name}-wh`" v-if="userState[member.user.id]">
              Work hours
            </b-th>
            <b-th class="text-center text-lowercase" :key="`${member.user.name}-lh`" v-if="userState[member.user.id]">
              Logged hours
            </b-th>
            <b-th class="text-center text-lowercase" :key="`${member.user.name}-ch`" v-if="userState[member.user.id]">
              Commercial hours
            </b-th>
          </template>
        </b-tr>
      </b-thead>
      <b-tbody>
        <b-tr v-for="(manager, i) in getManagers" :key="manager.name">
          <b-td class="text-center first-sticky" sticky-column>{{ manager.name }}</b-td>
          <b-td class="text-center second-sticky" sticky-column>{{ manager.logged_hours }}</b-td>
          <template v-for="member in manager.members">
            <b-td
              v-if="i === 0 && userState[member.id]"
              class="text-center group-border-right"
              :key="`${member.name}-wh-${manager.name}`"
              :rowspan="managers.length"
            >
              {{ getMemberByName(member.name).work_hours }}
            </b-td>
            <b-td
              v-if="userState[member.id]"
              class="text-center"
              :key="`${member.name}-lh-${manager.name}`"
            >
              {{ member.logged_hours }}
            </b-td>
            <b-td
              v-if="userState[member.id]"
              :key="`${member.name}-ch-${manager.name}`"
              class="text-center cursor-pointer position-relative variable-td"
              :variant="member.commercial_hours != member.logged_hours && +member.commercial_hours !== 0 ? 'danger' : ''"
              @dblclick="showChange(manager.id, member.id, member.commercial_hours)"
            >
              <span class="val" v-if="!tdChange[`change-${manager.id}-${member.id}`]">{{ member.commercial_hours }}</span>
              <div
                v-else
                class="d-flex justify-content-around align-items-center"
              >
                <span class="material-icons cancel" @click="hideChange(manager.id, member.id)">close</span>
                <b-form-input
                  class="input-change"
                  v-model="member.commercial_hours"
                  maxlength="5"
                  autofocus
                  @keyup.enter="saveChange(manager.id, member.id, member.commercial_hours)"
                  @keyup.esc="hideChange(manager.id, member.id)"
                />
                <span class="material-icons done" @click="saveChange(manager.id, member.id, member.commercial_hours)">done</span>
              </div>
            </b-td>
          </template>
        </b-tr>
      </b-tbody>
      <b-tfoot class="tfoot">
        <b-tr>
          <b-td class="text-center first-sticky" sticky-column>Total</b-td>
          <b-td class="text-center second-sticky" sticky-column>{{ getTotal | round(2) }}</b-td>
          <template v-for="member in getTotalData">
            <b-td
              v-if="userState[member.id]"
              class="text-center group-border-right"
              :key="`${member.id}-total-wh`"
              :rowspan="managers.length"
            >
              {{ member.total_wh | round(2) }}
            </b-td>
            <b-td
              v-if="userState[member.id]"
              class="text-center d-flex justify-content-center align-items-center"
              :key="`${member.id}-total-lh`"
              :variant="member.total_lh < member.total_wh ? 'danger' : ''"

            >
              {{ member.total_lh | round(2) }}
              <span class="material-icons cursor-pointer ml-2 info-icon" @click="getUserInfo(member)">
                info_outline
              </span>
            </b-td>
            <b-td
              v-if="userState[member.id]"
              :key="`${member.id}-total-ch`"
              class="text-center position-relative"
              :variant="member.total_ch !== member.total_lh && +member.total_ch !== 0 ? 'danger' : ''"
            >
              <span>{{ member.total_ch | round(2) }}</span>
            </b-td>
          </template>
        </b-tr>
      </b-tfoot>
    </b-table-simple>
    <div class="preloader" v-if="isDataLoading">
      <div class="spinner-border spinner-theme_default text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </div>
    <div v-if="!isDataLoading && (!managers.length || (managers.length && !getMembers.length))">
      No data
    </div>
  </div>
</template>

<script>
import { cloneDeep, uniqBy } from 'lodash'
import { mapGetters } from 'vuex'
import Moment from 'moment'
import Datepicker from 'vuejs-datepicker'
import TableUserItem from '@components/tables/TableUserItem'
import UsersStateListComponent from '@components/UsersStateListComponent'

export default {
  components: {
    TableUserItem,
    UsersStateListComponent,
    Datepicker
  },
  props: {
    currentWeek: {
      type: Object,
      default: () => ({})
    },
    managers: {
      type: Array,
      default: () => ([])
    },
    members: {
      type: Array,
      default: () => ([])
    },
    totalData: {
      type: Array,
      default: () => ([])
    },
    disabledBtn: {
      type: Number,
      default: 0
    },
    isDataLoading: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    disabledBtn (v) {
      this.disabledBtn = v
    },
    currentWeek: {
      handler (v) {
        this.dates.start = v.start
        this.dates.end = v.end
      },
      deep: true
    }
  },
  data () {
    return {
      userState: {},
      allActiveUsers: 0,
      uiTableName: 'hours_tracking_report',
      tdChange: {},
      dates: {
        start: null,
        end: null
      },
      dateChanged: false,
      tempValue: null,
      showIncorrectCommercialHoursOnly: false,
      showIncorrectLoggedHoursOnly: false,
      filteredIds: null
    }
  },
  mounted () {
    this.dates = cloneDeep(this.currentWeek)
  },
  computed: {
    ...mapGetters({
      dateFormat: 'dateFormat'
    }),
    getManagers () {
      const currentManagers = cloneDeep(this.managers)
      if (this.filteredIds) {
        currentManagers.forEach(manager => {
          manager.members = manager.members.filter(member => this.filteredIds.includes(member.id))
        })

        return currentManagers
      }

      return currentManagers
    },
    getMembers () {
      const currentMembers = cloneDeep(this.members)
      if (this.filteredIds) {
        return currentMembers.filter(member => this.filteredIds.includes(member.user.id))
      }

      return currentMembers
    },
    getTotalData () {
      const currentTotalData = cloneDeep(this.totalData)
      if (this.showIncorrectLoggedHoursOnly || this.showIncorrectCommercialHoursOnly) {
        let incorrectLoggedHours = []
        let incorrectCommercialHours = []
        if (this.showIncorrectLoggedHoursOnly) {
          incorrectLoggedHours = currentTotalData.filter(data => data.total_lh < data.total_wh)
        }
        if (this.showIncorrectCommercialHoursOnly) {
          incorrectCommercialHours = currentTotalData.filter(data => data.total_ch !== data.total_lh && +data.total_ch !== 0)
        }

        const filteredTotalData = uniqBy([...incorrectLoggedHours, ...incorrectCommercialHours])

        this.getFilteredIds(filteredTotalData)

        return filteredTotalData
      }

      this.getFilteredIds()

      return currentTotalData
    },
    getTotal () {
      return this.getSum(this.managers, 'logged_hours')
    },
    disabledFirstDate () {
      return {
        from: this.dates.end > new Date() ? new Date() : this.dates.end
      }
    },
    disabledSecondDate () {
      return {
        from: this.dates.end > new Date() ? this.dates.end : new Date(),
        ranges: [
          {
            from: new Date(2000, 1, 1),
            to: this.dates.start
          }
        ]
      }
    }
  },
  methods: {
    getMemberByName (name) {
      return this.members.find(member => member.user.name === name)
    },
    getSum (arr, key) {
      let sum = 0
      for (let i in arr) {
        sum += arr[i][key]
      }
      return sum
    },
    changeActive (disUsers) {
      const members = this.members
      for (const i in members) {
        if (disUsers.some(id => members[i].user.id === id)) {
          this.userState[members[i].user.id] = false
        } else {
          this.userState[members[i].user.id] = true
        }
      }
      this.allActiveUsers = Object.values(this.userState).filter(item => !!item).length
      this.$forceUpdate()
    },
    showChange (managerId, memberId, val) {
      this.tdChange = {}
      this.tdChange[`change-${managerId}-${memberId}`] = true
      this.tempValue = val

      this.$forceUpdate()
    },
    hideChange (managerId, memberId) {
      const curManager = this.managers.find(manager => manager.id === managerId)
      const curMember = curManager.members.find(member => member.id === memberId)

      curMember.commercial_hours = this.tempValue

      this.tdChange = {}
      this.tempValue = null
      this.$forceUpdate()
    },
    saveChange (managerId, memberId, hours) {
      this.axios
        .post('/api/finances/commercial-hours/save-to-hours-tracking-report', {
          date_yw: this.currentWeek.date_yw,
          manager_id: managerId,
          member_id: memberId,
          hours
        }, {
          headers: {
            Authorization: 'Bearer' + localStorage.getItem('access_token')
          }
        })
        .then(({ data }) => {
          this.$snotify.success(
            'Commercial hours successfully saved',
            'Success',
            this.$store.state.snotify
          )
        })
        .catch(error => {
          const errors = Object.values(error.response.data.errors)
          for (var i = 0; i < errors.length; i++) {
            this.$snotify.error(
              errors[i][0],
              'Error',
              this.$store.state.snotify
            )
          }
        })

      const managerIndex = this.managers.findIndex(manager => manager.id === managerId)
      const memberIndex = this.managers[managerIndex].members.findIndex(member => member.id === memberId)
      this.managers[managerIndex].members[memberIndex].commercial_hours = hours

      this.$emit('dataChanged')
      this.tdChange = {}
    },
    changeWeek (week) {
      this.$emit('changeWeek', week)
      this.dateChanged = false
    },
    selectDates (v, key) {
      this.dates[key] = v
      this.dateChanged = true
    },
    getFilteredIds (arr) {
      if (!arr) {
        this.filteredIds = null

        return
      }
      this.filteredIds = arr.map(item => item.id)
    },
    getUserInfo (member) {
      const dateFrom = Moment(this.dates.start).format('YYYY-MM-DD')
      const dateTo = Moment(this.dates.end).format('YYYY-MM-DD')

      const user = this.getMembers.find(userObj => userObj.user.id === member.id).user.jira_user_name

      this.$emit('getUserInfo', { users: [user], date_from: dateFrom, date_to: dateTo })
    }
  }
}
</script>

<style lang="scss" scoped>
  .table {
    &-container {
      background-color: #fff;
      padding: 10px;
      border-radius: 10px;
    }
    &__hours-tracking{
      &.table__theme-default table tbody tr {
        border-bottom: none;
      }
      .thead-light {
        th {
          background-color: #fff;
        }
      }

    }
  }
  .member-name {
    text-transform: none;
    max-width: 200px;
    min-width: 200px;

    ::v-deep.user-name {
      max-width: 140px;
    }
    ::v-deep.user-position {
      max-width: 120px;
    }
  }
  .group-border {
    &-left {
      border-left: 1px solid #dee2e6;
    }
    &-right {
      border-right: 1px solid #dee2e6;
    }
  }
  .input-change {
    min-height: 20px;
    max-width: 40px;
    margin: auto;
    padding: 0;
    text-align: center;
    line-height: 1;
  }
  .material-icons {
    &.done {
      color: #3FA52F;
    }
    &.cancel {
      color: #DC3545;
    }
  }
  .filter-dateweek {
    font-size: 14px;
    line-height: 22px;
    color: #3d5170;
    min-width: 85px;
    text-align: center;
  }
  .managers-column {
    min-width: 150px;
  }
  .tfoot {
    border-top: 1px solid #E4E7EE;
    font-weight: bold;
  }
  .filter-dateweek {
    ::v-deep.form-control {
      cursor: pointer !important;
    }
  }
  .variable-td {
    overflow: hidden;
    .val {
      font-weight: bold;
      text-decoration: underline;
    }
  }
  .first-sticky {
    max-width: 150px;
    background-color: #fff;
  }
  .second-sticky {
    left: 150px !important;
    background-color: #fff;
  }

  .info-icon {
    width: 14px;
    height: 14px;
    font-size: 14px;
  }
</style>
